import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiPlus, FiX } from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { readStoredUser } from "../utils/sessionUser";
import { createReservationTranslator, getReservationLocale, translateReservationStatus } from "./reservationI18n";
import { groupPendingExtrasMessages } from "./groupPendingExtrasMessages";
import "./GroupPendingExtrasModal.css";

const positiveId = (value) => /^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value));
const isDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
const datesValid = (values) => Array.isArray(values) && values.every(isDate) && new Set(values).size === values.length;
const memoryAttempts = new Map();
const changedSelection = (error) => Number(error.response?.status) === 409 && [
  "live_group_extra_preview_expired", "live_group_extra_dates_unavailable",
  "live_group_extra_selection_unavailable", "live_group_extra_membership_changed",
].includes(error.response?.data?.code);
const attemptKey = (scope) => `pilcore:pending-group-extras:${readStoredUser()?.id || "session"}:${scope}`;
const readAttempt = (key) => {
  try { return JSON.parse(window.sessionStorage.getItem(key) || "null"); }
  catch (_error) { return memoryAttempts.get(key) || null; }
};
const storeAttempt = (key, value) => {
  if (value) memoryAttempts.set(key, value); else memoryAttempts.delete(key);
  try {
    if (value) window.sessionStorage.setItem(key, JSON.stringify(value));
    else window.sessionStorage.removeItem(key);
  } catch (_error) { /* Keep the same operation in memory when storage is unavailable. */ }
};
const operationId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error("A secure operation identifier could not be created.");
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export function validateGroupExtraOptions(data, groupId, businessUnitId) {
  if (!data || Number(data.reservation_group_id) !== Number(groupId) ||
      Number(data.business_unit_id) !== Number(businessUnitId) ||
      !/^[A-Z]{3}$/.test(data.base_currency || "") || !isDate(data.business_date) ||
      !isDate(data.earliest_service_date) || !Array.isArray(data.reservations) || !Array.isArray(data.extras)) return false;
  const reservationIds = new Set(data.reservations.map((row) => Number(row.id)));
  const extraIds = new Set(data.extras.map((row) => Number(row.id)));
  return reservationIds.size === data.reservations.length && extraIds.size === data.extras.length &&
    data.reservations.every((row) => positiveId(row.id) && datesValid(row.eligible_stay_dates) && datesValid(row.blocked_stay_dates) &&
      row.eligible_stay_dates.every((date) => isDate(row.check_in_date) && isDate(row.check_out_date) &&
        date >= row.check_in_date && date < row.check_out_date && date >= data.earliest_service_date && !row.blocked_stay_dates.includes(date))) &&
    data.extras.every((extra) => positiveId(extra.id) && extra.scope === "per_reservation" &&
      ["per_night", "per_stay"].includes(extra.charge_period) && Array.isArray(extra.eligibility) &&
      new Set(extra.eligibility.map((row) => Number(row.reservation_id))).size === extra.eligibility.length &&
      extra.eligibility.every((row) => reservationIds.has(Number(row.reservation_id)) &&
        datesValid(row.eligible_stay_dates) && datesValid(row.existing_stay_dates) &&
        (row.remaining_max_nights == null || (Number.isInteger(row.remaining_max_nights) && row.remaining_max_nights >= 0))));
}

export function validateGroupExtraPreview(data, assignments, groupId, businessUnitId) {
  if (!Array.isArray(assignments) || !data || Number(data.reservation_group_id) !== Number(groupId) || Number(data.business_unit_id) !== Number(businessUnitId) ||
      !/^[A-Z]{3}$/.test(data.base_currency || "") || typeof data.preview_token !== "string" || !data.preview_token ||
      data.total_amount == null || !Number.isFinite(Number(data.total_amount)) || !Array.isArray(data.rows) ||
      data.rows.length !== assignments.length) return false;
  const keys = new Set();
  return data.rows.every((row) => {
    const key = `${row.reservation_id}:${row.extra_id}`;
    const requested = assignments.find((item) => Number(item.reservation_id) === Number(row.reservation_id) && Number(item.extra_id) === Number(row.extra_id));
    if (!requested || keys.has(key) || row.calculated_amount == null || !Number.isFinite(Number(row.calculated_amount))) return false;
    keys.add(key);
    return requested.service_date
      ? row.service_date === requested.service_date
      : datesValid(row.selected_stay_dates) && JSON.stringify([...row.selected_stay_dates].sort()) === JSON.stringify([...requested.selected_stay_dates].sort());
  });
}

export default function GroupPendingExtrasModal({ show, onClose, groupId, businessUnitId, onSaved }) {
  const { themeId, languageId } = usePmsTheme();
  const tr = useMemo(() => createReservationTranslator(languageId, groupPendingExtrasMessages), [languageId]);
  const locale = getReservationLocale(languageId);
  const validScope = positiveId(groupId) && positiveId(businessUnitId);
  const scope = validScope ? `${Number(groupId)}:${Number(businessUnitId)}` : "";
  const currentScope = useRef(scope);
  currentScope.current = show ? scope : "";
  const callbacks = useRef({ onClose, onSaved });
  callbacks.current = { onClose, onSaved };
  const dialog = useRef(null);
  const busy = useRef(false);
  const savingLock = useRef(false);
  const reloadNotice = useRef("");
  const [options, setOptions] = useState(null);
  const [loadedScope, setLoadedScope] = useState("");
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [picks, setPicks] = useState({});
  const [loadVersion, setLoadVersion] = useState(0);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingAttempt, setPendingAttempt] = useState(null);
  const [completed, setCompleted] = useState(false);
  busy.current = saving;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setOptions(null); setLoadedScope(""); setChosen([]); setPicks({}); setPreview(null);
    setPreviewError(""); setError(reloadNotice.current); reloadNotice.current = "";
    setCompleted(false); setLoading(false); setPendingAttempt(null);
    if (!show) return () => { active = false; controller.abort(); };
    if (!scope) { setError("The group or hotel is not valid."); return () => { active = false; }; }
    const stored = readAttempt(attemptKey(scope));
    if (/^[a-f0-9-]{36}$/i.test(stored?.body?.operation_id || "") && Array.isArray(stored.body.assignments) &&
        Number(stored.body.business_unit_id) === Number(businessUnitId) && stored.scope === scope) {
      setPendingAttempt({ ...stored, preview: validateGroupExtraPreview(stored.preview, stored.body.assignments, groupId, businessUnitId) ? stored.preview : null });
      setError("The operation could not be confirmed. Retry the same operation to check its result without adding duplicates.");
    }
    setLoading(true);
    axios.get(`/api/reservation-groups/${Number(groupId)}/extra-options`, {
      params: { business_unit_id: Number(businessUnitId) }, signal: controller.signal,
    }).then(({ data }) => {
      if (!active) return;
      if (!validateGroupExtraOptions(data, groupId, businessUnitId)) throw new Error("The extras response does not match this group and hotel.");
      setOptions(data); setLoadedScope(scope);
    }).catch((err) => {
      if (active) setError(err.message === "The extras response does not match this group and hotel."
        ? err.message : "Unable to load eligible extras.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [show, scope, groupId, businessUnitId, loadVersion]);

  const assignments = useMemo(() => Object.entries(picks).flatMap(([extraId, reservations]) =>
    Object.entries(reservations).filter(([, dates]) => dates.length).map(([reservationId, dates]) => ({
      reservation_id: Number(reservationId), extra_id: Number(extraId),
      ...(options?.extras.find((extra) => Number(extra.id) === Number(extraId))?.charge_period === "per_stay"
        ? { service_date: dates[0] } : { selected_stay_dates: [...dates].sort() }),
    }))).sort((a, b) => a.extra_id - b.extra_id || a.reservation_id - b.reservation_id), [picks, options]);
  const signature = JSON.stringify({ scope, assignments });
  const ready = show && scope && loadedScope === scope && options && !loading;
  const locked = !ready || saving || Boolean(pendingAttempt) || completed;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setPreview(null); setPreviewError(""); setPreviewLoading(false);
    if (!ready || !assignments.length || pendingAttempt || completed) return () => { active = false; };
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      axios.post(`/api/reservation-groups/${Number(groupId)}/extra-preview`, {
        business_unit_id: Number(businessUnitId), assignments,
      }, { signal: controller.signal }).then(({ data }) => {
        if (!active) return;
        if (!validateGroupExtraPreview(data, assignments, groupId, businessUnitId)) throw new Error("The price preview is incomplete. Nothing has been saved.");
        setPreview({ signature, data });
      }).catch((err) => {
        if (!active) return;
        if (changedSelection(err)) {
          reloadNotice.current = "Availability or prices changed. Review the updated options before confirming again.";
          setLoadVersion((value) => value + 1);
        } else setPreviewError(err.message === "The price preview is incomplete. Nothing has been saved."
          ? err.message : "Unable to calculate the additional charge. Change the selection or retry.");
      }).finally(() => { if (active) setPreviewLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [ready, signature, assignments, groupId, businessUnitId, pendingAttempt, completed, previewVersion]);

  useEffect(() => {
    if (!show || !dialog.current) return undefined;
    const previous = document.activeElement;
    const element = dialog.current;
    const focusable = () => [...element.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')];
    (focusable()[0] || element).focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy.current) { event.preventDefault(); callbacks.current.onClose?.(); }
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0] || element;
      const last = items[items.length - 1] || element;
      if (!element.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    element.addEventListener("keydown", onKeyDown);
    return () => { element.removeEventListener("keydown", onKeyDown); if (previous?.isConnected) previous.focus(); };
  }, [show]);

  const updatePick = (extraId, reservationId, dates) => {
    if (locked) return;
    setPicks((old) => ({ ...old, [extraId]: { ...old[extraId], [reservationId]: dates } }));
    setError("");
  };
  const removeExtra = (extraId) => {
    if (locked) return;
    setChosen((old) => old.filter((id) => id !== extraId));
    setPicks((old) => { const next = { ...old }; delete next[extraId]; return next; });
  };
  const money = (amount, currency = options?.base_currency) => new Intl.NumberFormat(locale, { style: "currency", currency: currency || "EUR" }).format(Number(amount));
  const dateLabel = (date) => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }).format(new Date(`${date}T12:00:00Z`));
  const reservationLabel = (row) => row.locator || tr("Reservation #{id}", { id: row.id });
  const visiblePreview = preview?.signature === signature ? preview.data : null;

  const save = async () => {
    if (savingLock.current || !show || !scope || completed || (!pendingAttempt && (!ready || !visiblePreview || previewLoading))) return;
    const scopeAtStart = scope;
    const key = attemptKey(scopeAtStart);
    let attempt = pendingAttempt;
    try {
      if (!attempt) attempt = { scope: scopeAtStart, preview: visiblePreview, body: {
        business_unit_id: Number(businessUnitId), assignments,
        operation_id: operationId(), preview_token: visiblePreview.preview_token,
      } };
    } catch (_error) { setError("A secure operation identifier could not be created."); return; }
    savingLock.current = true; setSaving(true); setError("");
    storeAttempt(key, attempt);
    try {
      const { data } = await axios.post(`/api/reservation-groups/${Number(groupId)}/add-extras`, attempt.body);
      if (data.operation_id !== attempt.body.operation_id || !validateGroupExtraPreview(data, attempt.body.assignments, groupId, businessUnitId)) {
        throw new Error("Unconfirmed response");
      }
      storeAttempt(key, null);
      if (currentScope.current !== scopeAtStart) return;
      setPendingAttempt(null); setCompleted(true);
      try { await callbacks.current.onSaved?.(data); callbacks.current.onClose?.(); }
      catch (_error) { setError("Extras were added, but the reservation view could not be refreshed. Close and reopen the group."); }
    } catch (err) {
      if (currentScope.current !== scopeAtStart) return;
      const status = Number(err.response?.status);
      if (!pendingAttempt && [400, 403, 404, 409, 422].includes(status)) {
        storeAttempt(key, null); setPendingAttempt(null);
        setPreview(null);
        if (changedSelection(err)) {
          reloadNotice.current = "Availability or prices changed. Review the updated options before confirming again.";
          setLoadVersion((value) => value + 1);
          setError(reloadNotice.current);
        } else setError("The operation was rejected. No extras were added.");
      } else {
        setPendingAttempt(attempt);
        setError("The operation could not be confirmed. Retry the same operation to check its result without adding duplicates.");
      }
    } finally { savingLock.current = false; setSaving(false); }
  };

  if (!show || typeof document === "undefined") return null;
  return createPortal(
    <div className={`gpe-overlay gpe-${themeId === "dark" ? "dark" : "light"}`}>
      <section className="gpe-dialog" ref={dialog} role="dialog" aria-modal="true" aria-labelledby="gpe-title" tabIndex={-1}>
        <header className="gpe-header">
          <div><p className="gpe-eyebrow">{tr("Group #{id}", { id: groupId })}</p><h2 id="gpe-title">{tr("Add extras to the group")}</h2><p>{tr("Existing extras and consumed nights will not be changed.")}</p></div>
          <button type="button" className="gpe-iconButton" aria-label={tr("Close extras dialog")} disabled={saving} onClick={onClose}><FiX aria-hidden="true" /></button>
        </header>
        <div className="gpe-content" aria-busy={loading || saving}>
          {error && <div className="gpe-error" role="alert">{tr(error)}</div>}
          {loading && <p role="status">{tr("Loading eligible extras…")}</p>}
          {!loading && !ready && scope && <button type="button" className="gpe-button" onClick={() => setLoadVersion((value) => value + 1)}>{tr("Retry loading")}</button>}
          {completed && <p role="status">{tr("Extras added successfully.")}</p>}
          {pendingAttempt?.preview && <section className="gpe-section gpe-preview"><h3>{tr("Operation awaiting confirmation")}</h3>
            {pendingAttempt.preview.rows.map((row) => <p key={`${row.reservation_id}:${row.extra_id}`}>
              <strong>{options?.reservations.find((item) => Number(item.id) === Number(row.reservation_id))?.locator || tr("Reservation #{id}", { id: row.reservation_id })}</strong>{" · "}{row.name}{" · "}{money(row.calculated_amount, pendingAttempt.preview.base_currency)}
            </p>)}
            <div className="gpe-total"><span>{tr("Additional total")}</span><strong>{money(pendingAttempt.preview.total_amount, pendingAttempt.preview.base_currency)}</strong></div>
          </section>}
          {ready && !pendingAttempt && <>
            <section className="gpe-section"><h3>{tr("1. Choose extras")}</h3>
              <label className="gpe-srOnly" htmlFor="gpe-extra-picker">{tr("Choose an extra…")}</label>
              <select id="gpe-extra-picker" value="" disabled={locked} onChange={(event) => {
                const id = Number(event.target.value);
                if (id && !chosen.includes(id)) setChosen((old) => [...old, id]);
              }}><option value="">{tr("Choose an extra…")}</option>{options.extras.filter((extra) => !chosen.includes(Number(extra.id))).map((extra) => <option key={extra.id} value={extra.id}>{extra.name} · {tr(extra.charge_period === "per_night" ? "Per night" : "Once per stay")}</option>)}</select>
              <p className="gpe-help">{tr("Fixed group extras are shared across the selected reservations; they are not multiplied by the room count.")}</p>
              {!options.extras.length && <p>{tr("No extras available for this group.")}</p>}
              {!options.reservations.some((row) => row.eligible_stay_dates.length) && <p>{tr("No eligible reservations in this group.")}</p>}
            </section>
            {chosen.length > 0 && <section className="gpe-section"><h3>{tr("2. Choose reservations and pending nights")}</h3>
              {chosen.map((extraId) => {
                const extra = options.extras.find((item) => Number(item.id) === extraId);
                return <article className="gpe-extra" key={extraId}>
                  <div className="gpe-extraHeader"><div><h4>{extra.name}</h4><span>{tr(extra.charge_period === "per_night" ? "Per night" : "Once per stay")}</span></div><button type="button" className="gpe-iconButton" disabled={locked} aria-label={tr("Remove {name}", { name: extra.name })} onClick={() => removeExtra(extraId)}><FiX aria-hidden="true" /></button></div>
                  <div className="gpe-reservations">{options.reservations.map((reservation) => {
                    const eligibility = extra.eligibility.find((row) => Number(row.reservation_id) === Number(reservation.id));
                    const available = !eligibility?.reason ? (eligibility?.eligible_stay_dates || []).filter((date) => reservation.eligible_stay_dates.includes(date) && !eligibility.existing_stay_dates.includes(date)) : [];
                    const selected = picks[extraId]?.[reservation.id] || [];
                    const maximum = eligibility?.remaining_max_nights ?? available.length;
                    const disabled = locked || !available.length || maximum === 0;
                    const label = reservationLabel(reservation);
                    return <div className={`gpe-reservation ${selected.length ? "gpe-reservation--selected" : ""}`} key={reservation.id}>
                      <div className="gpe-reservationHeading"><strong>{label}</strong><span>{translateReservationStatus(reservation.status, languageId)}</span></div>
                      {reservation.room_number && <div className="gpe-help">{tr("Room")} {reservation.room_number}</div>}
                      {available.length ? <>
                        <div className="gpe-dateSummary"><span>{tr("{count} pending nights", { count: available.length })}</span>{extra.charge_period === "per_night" && <button type="button" className="gpe-textButton" disabled={disabled} onClick={() => updatePick(extraId, reservation.id, selected.length ? [] : available.slice(0, maximum))}>{tr(selected.length ? "Clear nights" : "Select available nights")}</button>}</div>
                        {extra.charge_period === "per_stay" ? <label className="gpe-serviceDate">{tr("Service date")}<select disabled={disabled} value={selected[0] || ""} aria-label={`${tr("Service date")} · ${extra.name} · ${label}`} onChange={(event) => updatePick(extraId, reservation.id, event.target.value ? [event.target.value] : [])}><option value="">{tr("Choose a date…")}</option>{available.map((date) => <option key={date} value={date}>{dateLabel(date)}</option>)}</select></label>
                          : <div className="gpe-dates" role="group" aria-label={tr("Select {name} for {reservation}", { name: extra.name, reservation: label })}>{available.map((date) => <label key={date} className={`gpe-date ${selected.includes(date) ? "gpe-date--selected" : ""}`}><input type="checkbox" disabled={disabled || (!selected.includes(date) && selected.length >= maximum)} checked={selected.includes(date)} aria-label={`${extra.name} · ${label} · ${date}`} onChange={() => updatePick(extraId, reservation.id, selected.includes(date) ? selected.filter((item) => item !== date) : [...selected, date])} /><span>{dateLabel(date)}</span></label>)}</div>}
                        {eligibility.remaining_max_nights != null && <p className="gpe-help">{tr("Up to {count} additional nights", { count: maximum })}</p>}
                      </> : <p className="gpe-help">{tr(eligibility?.existing_stay_dates.length ? "This extra is already applied." : "Not eligible for this extra.")}</p>}
                      {reservation.blocked_stay_dates.length > 0 && <p className="gpe-help">{tr("{count} consumed or unavailable nights", { count: reservation.blocked_stay_dates.length })}</p>}
                      {eligibility?.existing_stay_dates.length > 0 && <p className="gpe-help">{tr("{count} nights already have this extra", { count: eligibility.existing_stay_dates.length })}</p>}
                    </div>;
                  })}</div>
                </article>;
              })}
            </section>}
            <section className="gpe-section gpe-preview" aria-live="polite"><h3>{tr("3. Review the additional charge")}</h3>
              <p className="gpe-help">{tr("The server calculates the exact amount, including eligibility and currency conversion.")}</p>
              {!assignments.length && <p>{tr("Select an extra and at least one eligible reservation/date.")}</p>}
              {previewLoading && <p role="status">{tr("Calculating additional charge…")}</p>}
              {previewError && <div className="gpe-error" role="alert">{tr(previewError)} <button type="button" className="gpe-textButton" onClick={() => setPreviewVersion((value) => value + 1)}>{tr("Retry calculation")}</button></div>}
              {visiblePreview && <><div className="gpe-tableWrap"><table><thead><tr><th>{tr("Reservation")}</th><th>{tr("Extra")}</th><th>{tr("Dates")}</th><th>{tr("Amount")}</th></tr></thead><tbody>{visiblePreview.rows.map((row) => <tr key={`${row.reservation_id}:${row.extra_id}`}><td>{reservationLabel(options.reservations.find((item) => Number(item.id) === Number(row.reservation_id)))}</td><td>{row.name}</td><td>{(row.service_date ? [row.service_date] : row.selected_stay_dates).map(dateLabel).join(", ")}</td><td>{money(row.calculated_amount, visiblePreview.base_currency)}</td></tr>)}</tbody></table></div><div className="gpe-total"><span>{tr("Additional total")}<small>{tr("{count} reservations", { count: new Set(assignments.map((row) => row.reservation_id)).size })}</small></span><strong>{money(visiblePreview.total_amount, visiblePreview.base_currency)}</strong></div></>}
            </section>
          </>}
        </div>
        <footer className="gpe-footer"><button type="button" className="gpe-button" disabled={saving} onClick={onClose}>{tr("Close")}</button><button type="button" className="gpe-button gpe-button--primary" disabled={saving || completed || (!pendingAttempt && (!ready || !visiblePreview || previewLoading || !assignments.length))} onClick={save}><FiPlus aria-hidden="true" />{tr(saving ? "Adding extras…" : pendingAttempt ? "Retry safely" : "Add selected extras")}</button></footer>
      </section>
    </div>, document.body
  );
}
